<!-- filepath: /src/components/PhysicianTypingDialog.vue -->
<template>
  <van-dialog
    v-model="localShow"
    title="医生分型问答"
    theme="round-button"
    confirm-button-color="#aa341b"
    :before-close="beforeClose"
    @confirm="onConfirm"
  >
    <div class="diglog-close" @click="closeDialog">×</div>
    <div class="diglog-questions">
      <div v-if="physicianName" class="physician-name">
        <span>医生：{{ physicianName }}</span>
      </div>
      <!-- 问卷选项绑定方式 -->
      <div v-for="(q, idx) in questions" :key="idx" class="question">
        <p class="question-text">
          {{ idx + 1 }}. {{ q.text }}
          <span style="color: #cc4b47">*</span>
        </p>
        <van-radio-group
          v-model="answers[idx].value"
          :direction="q.direction || 'vertical'"
          class="answer"
        >
          <van-radio v-for="opt in q.options" :key="opt.value" :name="opt.value">
            {{ opt.label }}
          </van-radio>
        </van-radio-group>
        <p v-if="showTips && !(answers[idx] && answers[idx].value)" class="error">这是必答题，请选择答案！</p>
      </div>
    </div>
  </van-dialog>
</template>

<script>
import { RadioGroup, Radio } from 'vant';
import * as py from '@/api/Physicians';
import * as moment from 'moment';
import { getLocalStorage } from '@/utils';

export default {
  name: 'PhysicianTypingDialog',
  components: {
    'van-radio-group': RadioGroup,
    'van-radio': Radio,
  },
  props: {
    show: Boolean,
    physicianId: {
      type: String,
      default: ''
    },
    physicianName: {
      type: String,
      default: ''
    }, // 新增
    productId: {
      type: String,
      default: ''
    },
    initialAnswers: {
      type: [Array, Object],
      default: () => []
    },
  },
  data() {
    return {
      localShow: this.show,
      answers: [],
      showTips: false,
      questions: [
        {
          text: '此位医生针对抑郁症的治疗目标',
          options: [
            { value: '1_symptom_remission', label: '仅关注控制情绪，躯体症状，尽量达到临床治愈' },
            { value: '2_cognitive_improvement', label: '关注认知症状，认为患者认知症状的改善也很重要' },
            { value: '3_functional_recovery', label: '以功能恢复为真正的治疗目标，全面改善患者的三位症状并兼顾安全性' }
          ]
        },
        {
          text: '此医生觉得哪些症状不太好解决，愿意试着想办法',
          options: [
            { value: '1_emotion', label: '情绪问题，尤其是快感缺失、情感迟钝\\低落等' },
            { value: '2_physical', label: '躯体症状如周身不适等' },
            { value: '3_cognitive', label: '认知功能如注意力、记忆力、决策力有问题影响工作或者学习等' }
          ]
        },
        {
          text: '此位医生目前将心达悦主要用在：',
          options: [
            { value: '1_high_function_group', label: '首选用于认知社会功能需要高的患者如工作人群或者学生人群' },
            { value: '2_switch_after_ssri_snri', label: '用于SSRI/SNRI使用后，情绪或认知症状仍改善不佳的患者的首选换药' },
            { value: '3_combination', label: '作为SSRI/SNRI使用不满意，但二联或三联以上使用' },
            { value: '4_other_mdd', label: '用于其他MDD患者' }
          ]
        },
        {
          text: '你对此位医生最近的工作重点：',
          options: [
            { value: '1_remission_and_recovery', label: '与其沟通：抑郁症治疗的总体目标是实现症状缓解(即临床治愈，定义为症状的完全或近乎完全消失)及全面功能恢复。建议考虑将心达悦作为症状缓解及功能恢复，尤其是以认知功能恢复为主诉的工作人群或者学生人群作为首选。' },
            { value: '2_switch_after_failure', label: '与其沟通：为了实现抑郁症治疗总体目标，建议考虑SSRI/SNRI疗效不佳后的心达悦的首选替代，从而提升临床治愈率及功能恢复率。' },
            { value: '3_adequate_dose_course', label: '推荐足剂量足疗程心达悦或20mg全面改善MDD患者症状缓解及功能恢复。' }
          ]
        },
        {
          text: '该医生是否在症状控制，以及功能恢复，尤其是认知功能恢复高需要的患者上使用20mg，并积累了积极的使用体会？',
          options: [
            { value: '1_yes', label: '是' },
            { value: '2_no', label: '否' }
          ],
          direction: 'horizontal'
        }
      ]
    };
  },
  watch: {
    show(val) {
      this.localShow = val;
      if (val) {
        // 初始化每题答案对象，确保长度和 questions 一致
        this.answers = this.questions.map((q, idx) => ({
          index: idx + 1,
          question: q.text,
          value: '',
          label: ''
        }));
        this.showTips = false;
      }
    },
    initialAnswers(val) {
      if (Array.isArray(val) && val.length === this.questions.length) {
        this.answers = val.map(item => ({ ...item }));
      }
    },
    localShow(val) {
      if (!val) {
        this.$emit('update:show', false);
      }
    }
  },
  created() {
    this.answers = this.questions.map((q, idx) => ({
      index: idx + 1,
      question: q.text,
      value: '',
      label: ''
    }));
  },
  methods: {
    closeDialog() {
      this.localShow = false;
      this.$emit('close');
    },
    // 选项绑定（在 onConfirm 时补全 label）
    onConfirm() {
      const allAnswered = this.answers.every(ans => ans.value);
      if (!allAnswered) {
        this.showTips = true;
        return;
      }
      // 补全 label
      this.answers.forEach((ans, idx) => {
        const opt = this.questions[idx].options.find(o => o.value === ans.value);
        ans.label = opt ? opt.label : '';
      });
      // 分型判断
      const type = this.getPhysicianType();
      // 提交
      let uName = getLocalStorage('user_name');
      let params = {
        physician_id: this.physicianId,
        content: JSON.stringify(this.answers),
        user_id: getLocalStorage('user_id'),
        territory_id: getLocalStorage('territory_id'),
        create_time: moment().format('YYYY-MM-DD HH:mm:ss'),
        product_id: this.productId,
        result: type,
        modify_user_id: getLocalStorage('user_id'),
        modify_user_name: uName !== 'null' ? uName : getLocalStorage('user_id'),
      };
      py.setPhysicianTyping(params).then((res) => {
        if (res.is_success) {
          this.$toast.success('提交成功');
          this.$emit('finish', { resultType: type, answers: [...this.answers] });
          this.closeDialog();
        }
      });
    },
    getPhysicianType() {
      const val = this.answers[0] && this.answers[0].value;
      if (val === '1_symptom_remission') return '观望者';
      if (val === '2_cognitive_improvement') return '尝试者';
      if (val === '3_functional_recovery') return '倡导者';
      return '观望者';
    },
    beforeClose(action, done) {
      if (action === 'confirm') {
        const allAnswered = this.answers.every(ans => ans.value);
        if (!allAnswered) {
          this.showTips = true;
          done(false);
        } else {
          done();
        }
      } else {
        done();
      }
    },
  }
};
</script>

<style scoped>
.diglog-close {
  position: absolute;
  font-size: 30px;
  top: 25px;
  right: 20px;
  cursor: pointer;
}
.physician-name {
  font-size: 14px;
  /* color: #aa341b; */
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: left;
}
.diglog-questions {
  max-height: 480px;
  overflow-y: auto;
  padding: 20px 20px 0 20px;
  text-align: left;
}
.question {
  margin-bottom: 20px;
}
.question-text {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
  line-height: 1.5;
}
.error {
  font-size: 14px;
  color: #aa341b;
  margin-top: 5px;
}
::v-deep .van-radio {
  font-size: 14px;
  align-items: flex-start;
  margin-bottom: 5px;
}
::v-deep .van-radio__icon--checked .van-icon,
::v-deep .van-checkbox__icon--checked .van-icon {
  background-color: #aa341b;
  border-color: #aa341b;
}
</style>
